"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.describeNetSuiteError = exports.logErrorFull = exports.isNetSuiteHttpError = exports.getNetSuiteErrorMessage = exports.buildNetSuiteHttpErrorMessage = exports.summarizeNetSuiteResponseBody = exports.extractNetSuiteErrorDetail = exports.redactBase64InJson = exports.truncateForLog = void 0;
/**
 * Truncate long strings for logs (avoid huge CloudWatch / terminal lines).
 */
function truncateForLog(value, maxLen) {
    if (value.length <= maxLen) {
        return value;
    }
    return `${value.slice(0, maxLen)}...[truncated ${value.length - maxLen} chars]`;
}
exports.truncateForLog = truncateForLog;
/**
 * JSON.stringify replacer that redacts known-binary fields (anything whose key
 * contains "base64", case-insensitive) so file-fetch responses don't dump
 * megabytes of base64 into CloudWatch / terminal logs. The length is preserved
 * for debugging. Use as the 2nd argument to JSON.stringify.
 *
 * Example:
 *   JSON.stringify(json, redactBase64InJson)
 *   → {"fileName":"x.pdf","base64Content":"[BASE64_REDACTED, 1564088 chars]"}
 */
function redactBase64InJson(key, value) {
    if (typeof value === "string" &&
        value.length > 100 &&
        /base64/i.test(key)) {
        return `[BASE64_REDACTED, ${value.length} chars]`;
    }
    return value;
}
exports.redactBase64InJson = redactBase64InJson;
/** Extract full error detail text from a NetSuite JSON error body (no truncation). */
function extractNetSuiteErrorDetail(body) {
    const trimmed = body.trim();
    if (!trimmed) {
        return "";
    }
    try {
        const j = JSON.parse(trimmed);
        if (typeof j.detail === "string" && j.detail.trim()) {
            return j.detail.trim();
        }
        const details = j["o:errorDetails"];
        if (Array.isArray(details) && details.length > 0) {
            const parts = [];
            for (let i = 0; i < details.length; i++) {
                const row = details[i];
                const d = (typeof row?.detail === "string" && row.detail) ||
                    (typeof row["o:detail"] === "string" && row["o:detail"]) ||
                    "";
                if (d.trim()) {
                    parts.push(d.trim());
                }
            }
            if (parts.length > 0) {
                return parts.join(" | ");
            }
        }
    }
    catch {
        // not JSON — return raw body below
    }
    return trimmed;
}
exports.extractNetSuiteErrorDetail = extractNetSuiteErrorDetail;
/**
 * Pull human-readable fields from NetSuite SuiteTalk JSON error bodies when present.
 */
function summarizeNetSuiteResponseBody(body, maxLenOrOptions = 2000) {
    const options = typeof maxLenOrOptions === "number"
        ? { maxLen: maxLenOrOptions }
        : maxLenOrOptions;
    const maxLen = options.maxLen ?? 2000;
    const truncateDetails = options.truncateDetails ?? true;
    const trimmed = body.trim();
    if (!trimmed) {
        return "(empty response body)";
    }
    try {
        const j = JSON.parse(trimmed);
        const parts = [];
        if (typeof j.title === "string") {
            parts.push(`title=${j.title}`);
        }
        if (typeof j.detail === "string") {
            parts.push(`detail=${truncateDetails ? truncateForLog(j.detail, 800) : j.detail}`);
        }
        if (typeof j.status === "number" || typeof j.status === "string") {
            parts.push(`status=${String(j.status)}`);
        }
        if (typeof j.type === "string") {
            parts.push(`type=${j.type}`);
        }
        const details = j["o:errorDetails"];
        if (Array.isArray(details) && details.length > 0) {
            const first = details[0];
            const d = (typeof first?.detail === "string" && first.detail) ||
                (typeof first["o:detail"] === "string" && first["o:detail"]) ||
                "";
            if (d) {
                parts.push(`o:errorDetails[0]=${truncateDetails ? truncateForLog(d, 800) : d}`);
            }
        }
        if (parts.length > 0) {
            return truncateDetails
                ? truncateForLog(parts.join(" | "), maxLen)
                : parts.join(" | ");
        }
    }
    catch {
        // not JSON
    }
    return truncateDetails ? truncateForLog(trimmed, maxLen) : trimmed;
}
exports.summarizeNetSuiteResponseBody = summarizeNetSuiteResponseBody;
/** Build a non-truncated HTTP error message for Error.message and downstream logs. */
function buildNetSuiteHttpErrorMessage(status, method, endpoint, responseBody) {
    const detail = extractNetSuiteErrorDetail(responseBody);
    if (detail) {
        return `NetSuite ${status} ${method} ${endpoint}: ${detail}`;
    }
    return `NetSuite ${status} ${method} ${endpoint}: ${responseBody.trim() || "(empty response body)"}`;
}
exports.buildNetSuiteHttpErrorMessage = buildNetSuiteHttpErrorMessage;
/**
 * Best full error text for logging, Jira, and DDB — prefers untruncated NetSuite detail.
 */
function getNetSuiteErrorMessage(error) {
    if (error instanceof Error) {
        const ne = error;
        if (ne.netSuiteResponseBody) {
            const detail = extractNetSuiteErrorDetail(ne.netSuiteResponseBody);
            if (detail) {
                return detail;
            }
        }
        return error.message;
    }
    return String(error);
}
exports.getNetSuiteErrorMessage = getNetSuiteErrorMessage;
function isNetSuiteHttpError(e) {
    return e instanceof Error && typeof e.netSuiteHttpStatus === "number";
}
exports.isNetSuiteHttpError = isNetSuiteHttpError;
/**
 * Log an error to console without truncating NetSuite response bodies or stacks.
 */
function logErrorFull(label, error) {
    console.error(`[${label}] --- error start ---`);
    if (error instanceof Error) {
        const fullMessage = getNetSuiteErrorMessage(error);
        console.error(`[${label}] message:`, fullMessage);
        if (fullMessage !== error.message &&
            error.message.includes("...[truncated")) {
            console.error(`[${label}] error.message (truncated):`, error.message);
        }
        else if (fullMessage !== error.message) {
            console.error(`[${label}] error.message (with HTTP context):`, error.message);
        }
        if (error.stack) {
            console.error(`[${label}] stack:`, error.stack);
        }
        const ne = error;
        if (ne.netSuiteHttpStatus != null) {
            console.error(`[${label}] httpStatus:`, ne.netSuiteHttpStatus);
        }
        if (ne.netSuiteMethod) {
            console.error(`[${label}] method:`, ne.netSuiteMethod);
        }
        if (ne.netSuiteEndpoint) {
            console.error(`[${label}] endpoint:`, ne.netSuiteEndpoint);
        }
        if (ne.netSuiteSummary) {
            console.error(`[${label}] summary:`, ne.netSuiteSummary);
        }
        if (ne.netSuiteResponseBody) {
            console.error(`[${label}] responseBody (${ne.netSuiteResponseBody.length} chars):`, ne.netSuiteResponseBody);
        }
        const cause = error.cause;
        if (cause !== undefined && cause !== null) {
            console.error(`[${label}] cause:`);
            logErrorFull(`${label}.cause`, cause);
        }
    }
    else {
        console.error(`[${label}]`, error);
    }
    console.error(`[${label}] --- error end ---`);
}
exports.logErrorFull = logErrorFull;
/**
 * Flatten error + optional NetSuite fields + Error.cause for console logging.
 */
function describeNetSuiteError(error, maxDepth = 4) {
    if (maxDepth <= 0) {
        return "(max depth)";
    }
    if (error instanceof Error) {
        const parts = [getNetSuiteErrorMessage(error)];
        const ne = error;
        if (ne.netSuiteHttpStatus != null) {
            parts.push(`http=${ne.netSuiteHttpStatus}`);
        }
        if (ne.netSuiteMethod && ne.netSuiteEndpoint) {
            parts.push(`${ne.netSuiteMethod} ${ne.netSuiteEndpoint}`);
        }
        if (ne.netSuiteSummary) {
            parts.push(`summary=${ne.netSuiteSummary}`);
        }
        if (ne.netSuiteResponseBody) {
            const detail = extractNetSuiteErrorDetail(ne.netSuiteResponseBody);
            parts.push(`body=${detail || ne.netSuiteResponseBody}`);
        }
        const cause = error.cause;
        if (cause !== undefined && cause !== null) {
            parts.push(`cause: ${describeNetSuiteError(cause, maxDepth - 1)}`);
        }
        return parts.join(" | ");
    }
    return String(error);
}
exports.describeNetSuiteError = describeNetSuiteError;
